// Import a CSV, preview it, then commit it.
//
// Ported from Zanetto Tools (phase 7 stage 2), with the API call lifted out:
// this takes an `onImport` callback rather than an endpoint, so the component
// has no coupling to a route and can be reused for resources, clients or
// anything else. Zanetto Tools also finished with `alert()`; the result is
// shown in the dialog here instead.
//
// It matters more in Dayboard than it does there. A new tenant starts empty,
// and typing thirty machines in one at a time is the difference between trying
// the product and bouncing off it (spec 10, onboarding).
import { useState } from 'react';
import { FileDrop } from './FileDrop';
import { Modal } from './Modal';

export interface CsvColumn {
  key: string;
  label: string;
}

export type CsvRow = Record<string, string>;

/**
 * Tokenise CSV text into a matrix of cells, handling quoted fields that may
 * contain commas, embedded quotes ("") and \r\n line endings.
 *
 * Exported because it is the part with rules in it, and the export is what
 * makes it testable the day the web app grows a test runner. Note this is the
 * READ side of the same RFC 4180 handling that phase 6b's export writes — if
 * one changes, check the other.
 */
export function tokenize(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field);
      field = '';
    } else if (ch === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else if (ch === '\r') {
      // Ignored — handled with the \n that follows it.
    } else {
      field += ch;
    }
  }

  if (field !== '' || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * A UTF-8 byte-order mark survives into the first header name and makes it
 * match nothing. Phase 6b's export writes one deliberately — Excel mangles
 * every accent without it — so a file that went out of Dayboard and came back
 * in would otherwise be rejected by its own first column.
 *
 * Compared by code point rather than written as a literal: the character is
 * invisible in an editor, and a zero-width thing you cannot see in a regex is
 * how this reads as a typo six months from now.
 */
const BOM = 0xfeff;
const stripBom = (s: string): string => (s.charCodeAt(0) === BOM ? s.slice(1) : s);

/**
 * Old spellings of a column, mapped to the one the caller works in.
 *
 * A header is a PARSE KEY, not a label, so renaming a column on screen would
 * silently stop importing the templates people already downloaded. Applied
 * before anything else looks at the row, so the preview and the import agree
 * (round 37, where People's "Category" became "Position").
 */
export type CsvAliases = Record<string, string>;

export function parseCsv(
  text: string,
  requiredHeaders: string[],
  aliases: CsvAliases = {}
): { rows: CsvRow[]; error: string | null } {
  const matrix = tokenize(text.trim());
  if (matrix.length < 2) return { rows: [], error: 'That file has a header row but no data.' };

  const headers = (matrix[0] ?? []).map((h) => {
    const name = stripBom(h).trim().toLowerCase();
    return aliases[name] ?? name;
  });

  const missing = requiredHeaders.filter((h) => !headers.includes(h));
  if (missing.length > 0) {
    return { rows: [], error: `That file is missing a column: ${missing.join(', ')}.` };
  }

  const rows = matrix
    .slice(1)
    .filter((cols) => cols.some((c) => c.trim() !== ''))
    .map((cols) => Object.fromEntries(headers.map((h, i) => [h, (cols[i] ?? '').trim()])));

  return { rows, error: null };
}

export function CsvImport({
  entityLabel,
  templateName,
  templateCsv,
  requiredHeaders,
  previewColumns,
  aliases = {},
  onImport,
  disabled = false,
  buttonClassName = 'btn-secondary btn-sm',
}: {
  /** Singular, lower case — "resource", "client". */
  entityLabel: string;
  templateName: string;
  templateCsv: string;
  requiredHeaders: string[];
  previewColumns: CsvColumn[];
  /** Old header spellings that still have to import. */
  aliases?: CsvAliases;
  /** Resolves with how many were imported, or throws with a readable message. */
  onImport: (rows: CsvRow[]) => Promise<number>;
  disabled?: boolean;
  /** Matches the page header's other buttons (round 45: Clients). */
  buttonClassName?: string;
}) {
  const [rows, setRows] = useState<CsvRow[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState<number | null>(null);

  const plural = (n: number) => `${n} ${entityLabel}${n === 1 ? '' : 's'}`;

  const downloadTemplate = () => {
    const blob = new Blob([templateCsv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = templateName;
    a.click();
    URL.revokeObjectURL(url);
  };

  // FileDrop clears its input after every pick, so the same file chosen twice
  // in a row still fires.
  const onFile = (file: File | undefined) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const { rows: parsed, error } = parseCsv(
        String(reader.result ?? ''),
        requiredHeaders,
        aliases
      );
      if (error) {
        setErrors([error]);
        setRows(null);
      } else {
        setRows(parsed);
        setErrors([]);
      }
    };
    reader.onerror = () => setErrors(['That file could not be read.']);
    reader.readAsText(file);
  };

  const commit = async () => {
    if (!rows) return;
    setImporting(true);
    setErrors([]);
    try {
      const imported = await onImport(rows);
      setRows(null);
      setDone(imported);
    } catch (reason) {
      setErrors([reason instanceof Error ? reason.message : 'That import did not go through.']);
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <button type="button" onClick={downloadTemplate} className={buttonClassName}>
        Download template
      </button>
      {/* A file dropped on the button imports like a chosen one (round 45). */}
      <FileDrop
        variant="button"
        accept=".csv,text/csv"
        typeLabel="CSV"
        buttonClassName={buttonClassName}
        disabled={disabled}
        label="Import CSV"
        onFiles={(files) => onFile(files[0])}
        onReject={(message) => setErrors([message])}
      />

      {/* Parse errors, when there is nothing to preview. */}
      {!rows && errors.length > 0 && (
        <Modal
          title="That file could not be imported"
          onClose={() => setErrors([])}
          dismissOnBackdrop
          footer={
            <button type="button" onClick={() => setErrors([])} className="btn-ghost btn-sm">
              Close
            </button>
          }
        >
          <div className="banner-error space-y-1">
            {errors.map((e) => (
              <div key={e}>{e}</div>
            ))}
          </div>
        </Modal>
      )}

      {done !== null && (
        <Modal
          title="Import finished"
          onClose={() => setDone(null)}
          dismissOnBackdrop
          footer={
            <button type="button" onClick={() => setDone(null)} className="btn-primary btn-sm">
              Done
            </button>
          }
        >
          <p className="text-sm text-gray-600">{plural(done)} added.</p>
        </Modal>
      )}

      {rows && (
        <Modal
          title={`Import ${plural(rows.length)}?`}
          onClose={() => setRows(null)}
          size="xl"
          footer={
            <>
              <button type="button" onClick={() => setRows(null)} className="btn-ghost btn-sm">
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void commit()}
                disabled={importing || rows.length === 0}
                className="btn-primary btn-sm"
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </>
          }
        >
          {errors.length > 0 && (
            <div className="banner-error mb-3 space-y-1">
              {errors.map((e) => (
                <div key={e}>{e}</div>
              ))}
            </div>
          )}

          <p className="mb-3 text-sm text-gray-600">
            Check this looks right before importing. Nothing is saved until you press Import.
          </p>

          <div className="overflow-x-auto rounded-md border border-gray-200">
            <table className="data-table text-xs">
              <thead>
                <tr>
                  {previewColumns.map((c) => (
                    <th key={c.key} scope="col" className="table-header">
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i}>
                    {previewColumns.map((c) => (
                      <td key={c.key} className="table-cell">
                        {r[c.key] ? r[c.key] : <span className="text-gray-500">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  );
}
